import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Users, UserCheck, UserMinus, Building2, 
  Plus, Search, LogOut, ChevronRight,
  FileText, ChevronDown, ChevronUp, X, Check, History,
  RefreshCw, Trash2, Edit2, AlertTriangle, CheckCircle2, XCircle
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';
import ChangeHistoryButton from '../components/ChangeHistoryButton';

import API from '../utils/api';

export default function ClientPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canWrite = user?.role === 'admin' || user?.effective_permissions?.clients === 'edit';
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [industryFilter, setIndustryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [clientsRes, statsRes, industriesRes] = await Promise.all([
        axios.get(`${API}/clients`, { 
          withCredentials: true,
          params: {
            industry: industryFilter && industryFilter !== 'all' ? industryFilter : undefined,
            status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
            search: search || undefined
          }
        }),
        axios.get(`${API}/clients/stats/summary`, { withCredentials: true }),
        axios.get(`${API}/industries`, { withCredentials: true })
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
      setIndustries(industriesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [industryFilter, statusFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading && !stats) {
    return (
      <SidebarLayout title="Clients" subtitle="Client Tracker">
        <div className="flex items-center justify-center py-20"><div className="text-white text-lg">Loading...</div></div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout title="Clients" subtitle="Client Tracker">
        {/* KPI Cards */}
        <section className="mb-8 animate-fadeIn">
          <div className="kpi-scroll md:grid-cols-3 gap-4">
            <KPICard icon={<Users className="text-indigo-400" />} label="Total Clients" value={stats?.total_clients || 0} color="indigo" testId="kpi-total" />
            <KPICard icon={<UserCheck className="text-emerald-400" />} label="Active" value={stats?.active_clients || 0} color="emerald" testId="kpi-active" />
            <KPICard icon={<UserMinus className="text-slate-400" />} label="Inactive" value={stats?.inactive_clients || 0} color="rose" testId="kpi-inactive" />
          </div>
        </section>

        {/* Industry Breakdown */}
        {stats?.industry_breakdown?.length > 0 && (
          <section className="mb-8 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="text-indigo-400" size={18} />
                <h3 className="font-semibold text-white">Industry Breakdown</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {stats.industry_breakdown.slice(0, 12).map((item, idx) => (
                  <div key={idx} className="bg-white/5 px-3 py-2 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-slate-300 truncate">{item.industry}</span>
                    <span className="text-lg font-bold text-white ml-2">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Search & Filters */}
        <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <Input type="text" placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-slate-500" data-testid="search-input" />
            </div>
            <div className="flex gap-3 flex-wrap">
              <Select value={industryFilter} onValueChange={setIndustryFilter}>
                <SelectTrigger className="w-[180px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[300px]">
                  <SelectItem value="all">All Industries</SelectItem>
                  {industries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              {canWrite && <Button onClick={() => setShowCreateDialog(true)} className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]" data-testid="create-client-button">
                <Plus size={18} className="mr-2" /> Create Client
              </Button>}
            </div>
          </div>
        </section>

        {/* Client Table */}
        <section className="glass-card overflow-hidden animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          <div className="overflow-x-auto mobile-cards">
            <table className="data-table" data-testid="client-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Industry</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-12 text-slate-500">No clients found</td></tr>
                ) : (
                  clients.map((client) => (
                    <tr key={client.id} className="cursor-pointer" onClick={() => setSelectedClient(client)} data-testid={`client-row-${client.client_id}`}>
                      <td>
                        <div>
                          <div className="text-white font-medium">{client.name}</div>
                          <div className="text-xs font-mono text-cyan-400">{client.client_id}</div>
                        </div>
                      </td>
                      <td><span className="text-xs px-2 py-1 rounded bg-indigo-500/10 text-indigo-400">{client.industry}</span></td>
                      <td><span className={`status-badge ${client.status?.toLowerCase()}`}>{client.status || 'Inactive'}</span></td>
                      <td>
                        <div className="flex items-center gap-1">
                          {user?.role === 'admin' && <ChangeHistoryButton resource="clients" recordId={client.client_id} />}
                          <ChevronRight size={16} className="text-slate-500" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

      {showCreateDialog && <CreateClientDialog industries={industries} onClose={() => setShowCreateDialog(false)} onCreated={fetchData} />}
      {selectedClient && <ClientDetailSheet client={selectedClient} onClose={() => setSelectedClient(null)} onUpdate={fetchData} canWrite={canWrite} />}
    </SidebarLayout>
  );
}

function KPICard({ icon, label, value, color, testId }) {
  const bgColors = { indigo: 'border-indigo-500/20', emerald: 'border-emerald-500/20', amber: 'border-amber-500/20', rose: 'border-rose-500/20', cyan: 'border-cyan-500/20' };
  return (
    <div className={`kpi-card border-l-4 ${bgColors[color]}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>
        {icon}
      </div>
      <div className="font-heading text-3xl font-black tracking-tight text-white">{value}</div>
    </div>
  );
}

// ================================
// CREATE CLIENT DIALOG — simplified (no status, no date)
// ================================
function CreateClientDialog({ industries, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddIndustry, setShowAddIndustry] = useState(false);
  const [newIndustry, setNewIndustry] = useState('');
  const [localIndustries, setLocalIndustries] = useState(industries);
  const [formData, setFormData] = useState({ name: '', industry: '', contact_name: '', phone: '', email: '' });

  const handleAddIndustry = async () => {
    if (!newIndustry.trim()) return;
    try {
      await axios.post(`${API}/industries`, { name: newIndustry.trim() }, { withCredentials: true });
      setLocalIndustries([...localIndustries, newIndustry.trim()].sort());
      setFormData({ ...formData, industry: newIndustry.trim() });
      setNewIndustry('');
      setShowAddIndustry(false);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { name: formData.name, industry: formData.industry };
      if (formData.contact_name) payload.contact_name = formData.contact_name;
      if (formData.phone) payload.phone = formData.phone;
      if (formData.email) payload.email = formData.email;
      await axios.post(`${API}/clients`, payload, { withCredentials: true });
      toast.success('Client created');
      onCreated();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail || 'Failed to create client');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10">
        <DialogHeader><DialogTitle className="text-white font-heading text-xl">Create Client</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {error && <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">{error}</div>}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Client Name *</label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Acme Corporation" className="bg-black/40 border-white/10 text-white" data-testid="create-client-name" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Industry *</label>
            {showAddIndustry ? (
              <div className="flex gap-2">
                <Input value={newIndustry} onChange={(e) => setNewIndustry(e.target.value)} placeholder="New industry name" className="bg-black/40 border-white/10 text-white flex-1" data-testid="new-industry-input" />
                <Button type="button" onClick={handleAddIndustry} className="bg-emerald-600 hover:bg-emerald-700">Add</Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddIndustry(false)} className="text-slate-400">Cancel</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Select value={formData.industry} onValueChange={(v) => setFormData({ ...formData, industry: v })}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white flex-1" data-testid="create-client-industry"><SelectValue placeholder="Select Industry" /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px]">{localIndustries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={() => setShowAddIndustry(true)} className="bg-white/5 border-white/10 text-slate-300"><Plus size={16} /></Button>
              </div>
            )}
          </div>
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Contact Details</h4>
            <div className="space-y-3">
              <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} placeholder="Primary Contact Name" className="bg-black/40 border-white/10 text-white" data-testid="create-client-contact" />
              <div className="grid grid-cols-2 gap-3">
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone Number" className="bg-black/40 border-white/10 text-white" data-testid="create-client-phone" />
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Email" className="bg-black/40 border-white/10 text-white" data-testid="create-client-email" />
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
            <Button type="submit" disabled={loading || !formData.name || !formData.industry} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-client">{loading ? 'Creating...' : 'Create Client'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ================================
// CLIENT DETAIL SHEET (WITH MULTIPLE SOW)
// ================================
function ClientDetailSheet({ client, onClose, onUpdate, canWrite }) {
  const navigate = useNavigate();
  const [scopes, setScopes] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [showScopeForm, setShowScopeForm] = useState(false);
  const [editingScope, setEditingScope] = useState(null);
  const [completionData, setCompletionData] = useState({});
  const [editingInfo, setEditingInfo] = useState(false);
  const [editForm, setEditForm] = useState({
    name: client.name || '',
    industry: client.industry || '',
    contact_name: client.contact_name || '',
    phone: client.phone || '',
    email: client.email || '',
  });
  const [savingInfo, setSavingInfo] = useState(false);
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    axios.get(`${API}/industries`, { withCredentials: true })
      .then(res => setIndustries(res.data))
      .catch(() => {});
  }, []);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const fetchScopes = useCallback(async () => {
    setScopeLoading(true);
    try {
      const res = await axios.get(`${API}/scopes`, { withCredentials: true, params: { client_id: client.client_id } });
      setScopes(res.data);

      // Fetch completion for active scopes
      const completions = {};
      for (const s of res.data.filter(sc => sc.status === 'Active')) {
        try {
          const trackRes = await axios.get(`${API}/deliverables/client/${client.client_id}`, { withCredentials: true, params: { scope_id: s.scope_id } });
          completions[s.scope_id] = trackRes.data.completion_pct || 0;
        } catch { completions[s.scope_id] = 0; }
      }
      setCompletionData(completions);
    } catch (err) { console.error(err); }
    finally { setScopeLoading(false); }
  }, [client.client_id]);

  useEffect(() => { fetchScopes(); }, [fetchScopes]);

  const handleDeleteClient = async () => {
    try {
      await axios.delete(`${API}/clients/${client.id}`, { withCredentials: true });
      toast.success('Client deleted');
      onUpdate();
      onClose();
    } catch (err) { toast.error('Failed to delete client'); }
  };

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      await axios.put(`${API}/clients/${client.id}`, editForm, { withCredentials: true });
      toast.success('Client info updated');
      setEditingInfo(false);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSavingInfo(false); }
  };

  const activeScopes = scopes.filter(s => s.status === 'Active');
  const inactiveScopes = scopes.filter(s => s.status !== 'Active');

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-white font-heading text-2xl font-bold" data-testid="client-detail-title">{editingInfo ? editForm.name : client.name}</SheetTitle>
          <div className="flex items-center gap-3">
            <span className="font-mono text-cyan-400 text-sm">{client.client_id}</span>
            <span className={`status-badge ${client.status?.toLowerCase() || 'inactive'}`}>{client.status || 'Inactive'}</span>
          </div>
        </SheetHeader>

        <div className="space-y-6">
          {/* Client Info */}
          <div className="glass-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400">Client Info</h3>
              {canWrite && !editingInfo && (
                <Button size="sm" variant="ghost" onClick={() => setEditingInfo(true)} className="text-slate-400 hover:text-white h-7 text-xs" data-testid="edit-client-info-btn">
                  <Edit2 size={12} className="mr-1" /> Edit
                </Button>
              )}
            </div>
            {editingInfo ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Name</label>
                    <Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-client-name" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Industry</label>
                    <Select value={editForm.industry} onValueChange={(v) => setEditForm(p => ({ ...p, industry: v }))}>
                      <SelectTrigger className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-client-industry">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f0f15] border-white/10">
                        {industries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Contact Name</label>
                    <Input value={editForm.contact_name} onChange={(e) => setEditForm(p => ({ ...p, contact_name: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-client-contact" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Phone</label>
                    <Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-client-phone" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Email</label>
                    <Input value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-client-email" />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={handleSaveInfo} disabled={savingInfo} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid="save-client-info-btn">
                    {savingInfo ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingInfo(false); setEditForm({ name: client.name || '', industry: client.industry || '', contact_name: client.contact_name || '', phone: client.phone || '', email: client.email || '' }); }} className="text-slate-400 h-7 text-xs" data-testid="cancel-edit-client-info">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <InfoRow label="Industry" value={<span className="text-xs px-2 py-1 rounded bg-indigo-500/10 text-indigo-400">{client.industry}</span>} />
                <InfoRow label="Contact" value={client.contact_name || '-'} />
                <InfoRow label="Phone" value={client.phone || '-'} />
                <InfoRow label="Email" value={client.email || '-'} />
              </div>
            )}
          </div>

          {/* SCOPE OF WORK SECTION */}
          <div data-testid="client-scope-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 flex items-center gap-2">
                <FileText size={14} className="text-indigo-400" /> Scope of Work ({scopes.length})
              </h3>
              {!showScopeForm && canWrite && (
                <Button size="sm" onClick={() => { setEditingScope(null); setShowScopeForm(true); }} className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs" data-testid="add-scope-button">
                  <Plus size={12} className="mr-1" /> Add Scope
                </Button>
              )}
            </div>

            {showScopeForm && (
              <div className="glass-card p-4 mb-4">
                <ScopeForm
                  clientId={client.client_id}
                  existingScope={editingScope}
                  onSaved={() => { setShowScopeForm(false); setEditingScope(null); fetchScopes(); onUpdate(); }}
                  onCancel={() => { setShowScopeForm(false); setEditingScope(null); }}
                />
              </div>
            )}

            {scopeLoading ? (
              <p className="text-slate-500 text-sm">Loading scopes...</p>
            ) : scopes.length === 0 && !showScopeForm ? (
              <div className="glass-card p-6 text-center">
                <p className="text-slate-500 text-sm">No scope of work defined. Add one to activate this client.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeScopes.map(scope => (
                  <ScopeCard key={scope.id} scope={scope} completionPct={completionData[scope.scope_id] || 0} onEdit={canWrite ? () => { setEditingScope(scope); setShowScopeForm(true); } : undefined} onRefresh={() => { fetchScopes(); onUpdate(); }} formatDate={formatDate} canWrite={canWrite} />
                ))}
                {inactiveScopes.length > 0 && (
                  <div className="pt-2">
                    <p className="text-xs text-slate-600 mb-2">PAST SCOPES</p>
                    {inactiveScopes.map(scope => (
                      <ScopeCard key={scope.id} scope={scope} completionPct={0} onRefresh={() => { fetchScopes(); onUpdate(); }} formatDate={formatDate} inactive canWrite={canWrite} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CLIENT MASTER SHEET — Link to full page */}
          <div className="glass-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-1">Client Master Sheet</h3>
                <p className="text-xs text-slate-500">Credentials, social links, team, assets, shoots, branding & more</p>
              </div>
              <Button size="sm" onClick={() => { onClose(); setTimeout(() => navigate(`/clients/${client.client_id}/master`), 100); }} className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="view-master-sheet-button">
                View Master Sheet
              </Button>
            </div>
          </div>

          {/* Delete Client */}
          {canWrite && (
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <Button variant="destructive" onClick={handleDeleteClient} className="flex-1 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20" data-testid="delete-client-button">
                <Trash2 size={16} className="mr-2" /> Delete Client
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ================================
// SCOPE CARD (single scope within client detail)
// ================================
function ScopeCard({ scope, completionPct, onEdit, onRefresh, formatDate, inactive, canWrite }) {
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const isRetainer = scope.engagement_type === 'Retainer';
  const isProject = scope.engagement_type === 'Project';
  const isOverdue = isRetainer && scope.next_renewal_date && new Date(scope.next_renewal_date) < new Date();

  const loadVersions = async () => {
    if (versions.length > 0) { setShowVersions(!showVersions); return; }
    try {
      const res = await axios.get(`${API}/scopes/${scope.id}/versions`, { withCredentials: true });
      setVersions(res.data);
      setShowVersions(true);
    } catch (err) { console.error(err); }
  };

  const handleRenew = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/scopes/${scope.id}/renew`, {}, { withCredentials: true });
      toast.success('Scope renewed — tracking reset for new cycle');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleNotRenew = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/scopes/${scope.id}/not-renew`, {}, { withCredentials: true });
      toast.success('Scope marked as not renewed');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/scopes/${scope.id}/complete`, {}, { withCredentials: true });
      toast.success('Project marked as completed');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/scopes/${scope.id}`, { withCredentials: true });
      toast.success('Scope deleted');
      onRefresh();
    } catch (err) { toast.error('Failed'); }
  };

  // Date range
  const dateRange = isRetainer
    ? `${formatDate(scope.start_date)} — ${formatDate(scope.next_renewal_date)}`
    : `${formatDate(scope.start_date)} — ${formatDate(scope.end_date)}`;

  return (
    <div className={`glass-card p-4 ${inactive ? 'opacity-60' : ''} ${isOverdue ? 'border-rose-500/20' : ''}`} data-testid={`scope-card-${scope.scope_id}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded ${isRetainer ? 'bg-cyan-500/10 text-cyan-400' : 'bg-purple-500/10 text-purple-400'}`}>{scope.engagement_type}</span>
            <span className={`status-badge text-[10px] ${scope.status.toLowerCase()}`}>{scope.status}</span>
            {isOverdue && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-400 font-semibold animate-pulse">OVERDUE</span>}
          </div>
          <p className="text-xs text-slate-500 font-mono">{scope.scope_id} {'\u00B7'} V{scope.version || 1}</p>
          <p className="text-xs text-slate-500 mt-0.5">{dateRange}{isRetainer ? ` \u00B7 ${scope.renewal_cycle}` : ''}</p>
        </div>
        {!inactive && onEdit && (
          <Button size="sm" variant="ghost" onClick={onEdit} className="text-slate-400 hover:text-white h-7 text-xs" data-testid={`edit-scope-${scope.scope_id}`}>
            <Edit2 size={12} className="mr-1" /> Edit
          </Button>
        )}
      </div>

      {/* Completion bar (active only) */}
      {!inactive && (
        <div className="flex items-center gap-2 mb-3">
          <Progress value={completionPct} className="h-1.5 flex-1" />
          <span className="text-xs font-bold text-white min-w-[35px] text-right">{completionPct}%</span>
        </div>
      )}

      {/* Deliverables */}
      {scope.deliverables?.length > 0 && (
        <div className="space-y-1 mb-3">
          {scope.deliverables.map((d, i) => (
            <div key={i} className="flex items-center justify-between py-1 px-2 rounded bg-white/[0.02] text-xs">
              <span className="text-slate-300">{d.type} <span className="text-slate-600">{d.category}</span></span>
              <span className="text-slate-500">{d.quantity}x {d.frequency}</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions (active scopes only) */}
      {!inactive && scope.status === 'Active' && canWrite && (
        <div className="flex gap-2 mb-2">
          {isRetainer && (
            <>
              <Button size="sm" onClick={handleRenew} disabled={actionLoading} className="bg-cyan-600 hover:bg-cyan-700 h-7 text-xs flex-1" data-testid={`renew-${scope.scope_id}`}>
                <RefreshCw size={12} className="mr-1" /> Renew
              </Button>
              <Button size="sm" variant="outline" onClick={handleNotRenew} disabled={actionLoading} className="bg-white/5 border-white/10 text-slate-300 hover:bg-rose-500/10 hover:text-rose-400 h-7 text-xs flex-1" data-testid={`not-renew-${scope.scope_id}`}>
                <XCircle size={12} className="mr-1" /> Not Renewed
              </Button>
            </>
          )}
          {isProject && (
            <Button size="sm" onClick={handleComplete} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs flex-1" data-testid={`complete-${scope.scope_id}`}>
              <CheckCircle2 size={12} className="mr-1" /> Mark as Complete
            </Button>
          )}
        </div>
      )}

      {/* Version history + Delete */}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <button className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1" onClick={loadVersions}>
          <History size={10} /> Versions ({scope.version || 1})
          {showVersions ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
        {canWrite && (
          <button onClick={handleDelete} className="text-xs text-rose-400/50 hover:text-rose-400" data-testid={`delete-scope-${scope.scope_id}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {showVersions && versions.length > 0 && (
        <div className="mt-2 space-y-1">
          {versions.map(v => (
            <div key={v.id} className="flex items-start gap-2 p-1.5 rounded bg-white/[0.02]">
              <span className="text-[10px] font-mono bg-indigo-500/20 text-indigo-300 px-1 py-0.5 rounded shrink-0">V{v.version}</span>
              <div>
                <p className="text-[10px] text-slate-300">{v.change_description}</p>
                <p className="text-[10px] text-slate-600">{formatDate(v.change_date)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================
// SCOPE FORM (CREATE / EDIT)
// ================================
function ScopeForm({ clientId, existingScope, onSaved, onCancel }) {
  const [masterData, setMasterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    engagement_type: existingScope?.engagement_type || '',
    start_date: existingScope?.start_date || '',
    renewal_cycle: existingScope?.renewal_cycle || '',
    end_date: existingScope?.end_date || '',
  });
  const [deliverables, setDeliverables] = useState(existingScope?.deliverables?.map(d => ({...d})) || []);
  const [changeDesc, setChangeDesc] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [customName, setCustomName] = useState('');

  useEffect(() => {
    axios.get(`${API}/scopes/master-deliverables`, { withCredentials: true }).then(res => setMasterData(res.data)).catch(console.error);
  }, []);

  const addDeliverable = (category, type) => {
    const isContent = masterData?.content_deliverables?.includes(type);
    const isAd = masterData?.ad_deliverables?.includes(type);
    setDeliverables(prev => [...prev, { category, type, quantity: 1, frequency: 'Monthly', posted_by_us: isContent ? false : null, platforms: isAd ? [type] : [] }]);
  };

  const updateDel = (idx, field, value) => {
    setDeliverables(prev => prev.map((d, i) => {
      if (i !== idx) return d;
      const u = { ...d, [field]: value };
      if (field === 'posted_by_us' && !value) u.platforms = [];
      return u;
    }));
  };

  const togglePlatform = (idx, platform) => {
    setDeliverables(prev => prev.map((d, i) => {
      if (i !== idx) return d;
      const has = d.platforms?.includes(platform);
      return { ...d, platforms: has ? d.platforms.filter(p => p !== platform) : [...(d.platforms || []), platform] };
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      if (existingScope) {
        const payload = { ...formData, deliverables, change_description: changeDesc || 'Scope updated' };
        if (formData.engagement_type !== 'Retainer') { payload.renewal_cycle = null; }
        if (formData.engagement_type !== 'Project') { payload.end_date = null; }
        await axios.put(`${API}/scopes/${existingScope.id}`, payload, { withCredentials: true });
        toast.success('Scope updated');
      } else {
        const payload = { client_id: clientId, ...formData, deliverables, status: 'Active' };
        if (formData.engagement_type !== 'Retainer') delete payload.renewal_cycle;
        if (formData.engagement_type !== 'Project') delete payload.end_date;
        await axios.post(`${API}/scopes`, payload, { withCredentials: true });
        toast.success('Scope created');
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail || 'Failed');
    } finally { setLoading(false); }
  };

  const canSubmit = formData.engagement_type && formData.start_date &&
    (formData.engagement_type === 'Retainer' ? formData.renewal_cycle : formData.end_date) &&
    deliverables.length > 0;

  const addedTypes = deliverables.map(d => d.type);

  return (
    <div className="space-y-4" data-testid="scope-form">
      {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Type *</label>
          <Select value={formData.engagement_type} onValueChange={(v) => setFormData({ ...formData, engagement_type: v, renewal_cycle: '', end_date: '' })}>
            <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="scope-engagement-type"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
              <SelectItem value="Retainer">Retainer</SelectItem>
              <SelectItem value="Project">Project</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Start Date *</label>
          <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="scope-start-date" />
        </div>
        {formData.engagement_type === 'Retainer' && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Renewal Cycle *</label>
            <Select value={formData.renewal_cycle} onValueChange={(v) => setFormData({ ...formData, renewal_cycle: v })}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="scope-renewal-cycle"><SelectValue placeholder="Cycle" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                {masterData?.renewal_cycles?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {formData.engagement_type === 'Project' && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">End Date *</label>
            <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="scope-end-date" />
          </div>
        )}
      </div>

      {/* Added deliverables */}
      {deliverables.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs text-slate-500 font-semibold">DELIVERABLES ({deliverables.length})</h4>
          {deliverables.map((d, idx) => {
            const isContent = masterData?.content_deliverables?.includes(d.type);
            const isAd = masterData?.ad_deliverables?.includes(d.type);
            return (
              <div key={idx} className="bg-white/[0.02] border border-white/5 rounded-lg p-2.5 space-y-2" data-testid={`deliverable-row-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-white text-xs font-medium">{d.type} <span className="text-slate-600">{d.category}</span></span>
                  <button onClick={() => setDeliverables(prev => prev.filter((_, i) => i !== idx))} className="text-slate-500 hover:text-rose-400"><X size={14} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-600 block">Qty</label>
                    <Input type="number" min={1} value={d.quantity} onChange={(e) => updateDel(idx, 'quantity', parseInt(e.target.value) || 1)} className="bg-black/40 border-white/10 text-white h-7 text-xs" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-600 block">Freq</label>
                    <Select value={d.frequency} onValueChange={(v) => updateDel(idx, 'frequency', v)}>
                      <SelectTrigger className="bg-black/40 border-white/10 text-white h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="One-time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {isContent && (
                  <div>
                    <label className="text-[10px] text-slate-600 block mb-1">Posted by us?</label>
                    <div className="flex gap-1.5 mb-1">
                      <button type="button" className={`text-[10px] px-2 py-0.5 rounded ${d.posted_by_us === true ? 'bg-emerald-600 text-white' : 'bg-white/5 text-slate-400 border border-white/10'}`} onClick={() => updateDel(idx, 'posted_by_us', true)}>Yes</button>
                      <button type="button" className={`text-[10px] px-2 py-0.5 rounded ${d.posted_by_us === false ? 'bg-slate-600 text-white' : 'bg-white/5 text-slate-400 border border-white/10'}`} onClick={() => updateDel(idx, 'posted_by_us', false)}>No</button>
                    </div>
                    {d.posted_by_us && (
                      <div className="flex flex-wrap gap-1">
                        {masterData?.content_platforms?.map(p => (
                          <button key={p} type="button" onClick={() => togglePlatform(idx, p)} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${d.platforms?.includes(p) ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300' : 'bg-white/5 border-white/10 text-slate-400'}`}>{p}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {isAd && (
                  <div>
                    <label className="text-[10px] text-slate-600 block mb-1">Platforms</label>
                    <div className="flex flex-wrap gap-1">
                      {masterData?.ad_platforms?.map(p => (
                        <button key={p} type="button" onClick={() => togglePlatform(idx, p)} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${d.platforms?.includes(p) ? 'bg-purple-500/20 border-purple-500/30 text-purple-300' : 'bg-white/5 border-white/10 text-slate-400'}`}>{p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Deliverable picker */}
      <div className="border-t border-white/5 pt-3">
        <h4 className="text-xs text-slate-500 font-semibold mb-2">ADD DELIVERABLES</h4>
        <div className="space-y-0.5">
          {masterData && Object.entries(masterData.categories).map(([cat, types]) => (
            <div key={cat}>
              <button type="button" className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-white/5" onClick={() => setExpanded(expanded === cat ? null : cat)}>
                <span className="text-xs font-medium text-slate-300">{cat}</span>
                {expanded === cat ? <ChevronUp size={12} className="text-slate-500" /> : <ChevronDown size={12} className="text-slate-500" />}
              </button>
              {expanded === cat && (
                <div className="pl-2 pb-1 flex flex-wrap gap-1.5">
                  {types.map(type => {
                    const added = addedTypes.includes(type);
                    return (
                      <button key={type} type="button" disabled={added}
                        className={`text-[10px] px-2 py-1 rounded-lg border ${added ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-white/5 border-white/10 text-slate-300 hover:bg-indigo-500/10 hover:text-indigo-300'}`}
                        onClick={() => !added && addDeliverable(cat, type)}
                        data-testid={`add-del-${type.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                      >{added ? <><Check size={8} className="inline mr-0.5" />{type}</> : <><Plus size={8} className="inline mr-0.5" />{type}</>}</button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-1.5 px-2 pt-1">
            <Input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Custom deliverable..." className="bg-black/40 border-white/10 text-white h-7 text-xs flex-1" data-testid="custom-deliverable-input" />
            <Button type="button" size="sm" disabled={!customName.trim()} className="bg-indigo-600 hover:bg-indigo-700 h-7 text-[10px]" onClick={() => { addDeliverable('CUSTOM', customName.trim()); setCustomName(''); }}>Add</Button>
          </div>
        </div>
      </div>

      {existingScope && (
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Change Description</label>
          <Input value={changeDesc} onChange={(e) => setChangeDesc(e.target.value)} placeholder="What changed?" className="bg-black/40 border-white/10 text-white h-8 text-sm" data-testid="scope-change-desc" />
        </div>
      )}

      <div className="flex flex-col gap-2 pt-2">
        {!canSubmit && (
          <div className="text-[10px] text-amber-300/80 px-2.5 py-1.5 rounded bg-amber-500/5 border border-amber-500/15 flex items-start gap-1.5" data-testid="scope-validation-hint">
            <span>⚠</span>
            <span>
              {[
                !formData.engagement_type && 'engagement type',
                !formData.start_date && 'start date',
                formData.engagement_type === 'Retainer' && !formData.renewal_cycle && 'renewal cycle',
                formData.engagement_type === 'Project' && !formData.end_date && 'end date',
                deliverables.length === 0 && 'at least one deliverable',
              ].filter(Boolean).reduce((acc, m, i, arr) => acc + (i === 0 ? 'Add ' : i === arr.length - 1 ? ' and ' : ', ') + m, '')}
              {' '}to enable Create.
            </span>
          </div>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} size="sm" className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !canSubmit} onClick={handleSubmit} size="sm" className="bg-indigo-600 hover:bg-indigo-700 flex-1 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="scope-submit">
            {loading ? 'Saving...' : existingScope ? 'Update Scope' : 'Create Scope'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500 text-sm">{label}</span>
      <span className="text-slate-200 text-sm">{value}</span>
    </div>
  );
}
